import { useState } from "react"
import { SortableContext, useSortable } from "@dnd-kit/sortable"
import ActivitiesWeeklyCardSortable from "./activities-weekly-card-sortable"
import type { Activities as Column } from "../../../entities/activities"
import { intervalToDuration, isFuture, isToday, isBefore } from "date-fns"
import { formatDuration } from "@/shared/utils/date-fn"
import { cn } from "@/lib/utils"

interface ActivitiesWeeklyColumnProps {
  day: string
  date: string
  column: Column
  isActive: boolean
  totalSeconds: number
  isCurrentDay: boolean
  isRescheduled: boolean
  isDragging: boolean
  isPending: boolean
  sourceColumn: Column | null
  isOverloaded: boolean
}

export default function ActivitiesWeeklyColumn({
  day,
  date: dateString,
  column,
  isActive,
  totalSeconds,
  isCurrentDay,
  isRescheduled,
  isDragging,
  isPending,
  sourceColumn,
  isOverloaded,
}: ActivitiesWeeklyColumnProps) {
  // column is only available if its date is today or in the future.
  const date = new Date(dateString)
  const isAvailableDay = isToday(date) || isFuture(date)
  const isNotAvailableDay = !isAvailableDay
  const isAnOldColumn = isBefore(date, new Date()) && !isToday(date)
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const { setNodeRef } = useSortable({
    id: `column-${column.id}`,
    data: {
      type: "column",
      column,
    },
  })

  const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 })
  const formattedDuration = formatDuration(duration).min()

  //TODO use date fns, and handle this globally
  const formattedDay = new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    timeZone: "America/Argentina/Buenos_Aires",
  }).format(date)

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "h-full min-h-[600px] w-full select-none rounded-[10px] p-3 md:w-[300px]",
        isRescheduled && "bg-neutral-200",
        isCurrentDay && "bg-neutral-200",
        isActive
          ? "border-2 border-primary bg-primary-50"
          : isDragging
            ? "border-2 border-primary"
            : "border-2 border-transparent bg-neutral-100",
        isAnOldColumn &&
          isDragging &&
          "pointer-events-none border-transparent bg-neutral-100 opacity-50",
      )}
    >
      <div className="flex w-full justify-between px-1">
        <p
          style={{
            textTransform: "capitalize",
            textDecoration: isNotAvailableDay ? "line-through" : "none",
            textDecorationColor: "gray",
          }}
          className={"text-md mb-10 flex items-center gap-2 font-normal"}
        >
          <span className="flex items-center gap-2">
            {isCurrentDay ? (
              <span className={cn("capitalize", isDragging && "text-primary")}>{day}</span>
            ) : null}
            <span
              className={cn(
                "flex h-8 items-center justify-center",
                isNotAvailableDay && "text-neutral-500/50",
                isCurrentDay
                  ? "w-8 rounded-full bg-primary text-xs font-bold text-white"
                  : cn(
                      "text-md bg-transparent font-medium",
                      isDragging && !isNotAvailableDay ? "text-primary" : "text-neutral-500",
                    ),
              )}
            >
              {isCurrentDay ? formattedDay : `${day} ${formattedDay}`}
            </span>
          </span>
        </p>
        {formattedDuration ? (
          <div
            className="relative inline-block"
            onMouseEnter={() => setIsTooltipVisible(true)}
            onMouseLeave={() => setIsTooltipVisible(false)}
          >
            <p
              className={cn(
                "text-md font-normal",
                isNotAvailableDay ? "text-neutral-500/60" : "text-neutral-500",
                isOverloaded && "flex h-fit items-center justify-center gap-2 text-destructive",
              )}
            >
              {isOverloaded && (
                <span className="inline-block h-2.5 w-2.5 animate-pulse-shadow rounded-full bg-destructive"></span>
              )}
              {formattedDuration}
            </p>

            {isTooltipVisible && isOverloaded && (
              <div className="absolute right-[80px] top-[65px] z-10 w-[200px] -translate-y-full transform overflow-hidden rounded-lg border border-neutral-200 bg-white p-2 text-xs shadow-lg">
                <p>Superaste las horas de estudio declaradas. Ajusta tus horas en Mi agenda.</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="relative top-[-15px] flex flex-col space-y-2">
        <SortableContext items={column.activities.map((act) => act.id)} strategy={() => null}>
          {column.activities.map((activity) => (
            <ActivitiesWeeklyCardSortable
              key={activity.id}
              activity={activity}
              areDragging={isDragging}
              isPending={isPending}
              columnDate={column.date}
              sourceColumn={sourceColumn}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  )
}
